import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { getGuestBookings } from '../../lib/api';
import { Calendar, MapPin, ArrowRight, Loader2 } from 'lucide-react';
import { format } from 'date-fns';

export default function GuestDashboard() {
  const { user, isHost } = useAuth();
  const [recentBookings, setRecentBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        const response = await getGuestBookings({ limit: 5 });
        setRecentBookings(response.data.items || []);
      } catch (error) {
        console.error('Failed to load bookings:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const formatPrice = (cents) => {
    return (cents / 100).toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    });
  };

  return (
    <div className="space-y-8" data-testid="guest-dashboard">
      {/* Welcome Header */}
      <div>
        <h1 className="font-heading text-2xl font-bold text-slate-900">
          Welcome back, {user?.name?.split(' ')[0]}!
        </h1>
        <p className="text-slate-500 mt-1">
          Manage your bookings and explore Southwest Florida.
        </p>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Link to="/search">
          <Card className="hover:shadow-md transition-shadow cursor-pointer">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                <MapPin className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h3 className="font-medium text-slate-900">Browse Properties</h3>
                <p className="text-sm text-slate-500">Find your next getaway</p>
              </div>
            </CardContent>
          </Card>
        </Link>

        <Link to="/app/bookings">
          <Card className="hover:shadow-md transition-shadow cursor-pointer">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                <Calendar className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h3 className="font-medium text-slate-900">My Bookings</h3>
                <p className="text-sm text-slate-500">View all reservations</p>
              </div>
            </CardContent>
          </Card>
        </Link>

        {isHost && (
          <Link to="/app/host">
            <Card className="hover:shadow-md transition-shadow cursor-pointer bg-primary text-white">
              <CardContent className="p-6 flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                  <ArrowRight className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-medium">Host Dashboard</h3>
                  <p className="text-sm text-white/80">Manage your properties</p>
                </div>
              </CardContent>
            </Card>
          </Link>
        )}
      </div>

      {/* Recent Bookings */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="font-heading">Recent Bookings</CardTitle>
          <Link to="/app/bookings">
            <Button variant="ghost" size="sm">
              View All
              <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
            </div>
          ) : recentBookings.length > 0 ? (
            <div className="space-y-4">
              {recentBookings.map((booking) => (
                <Link 
                  key={booking.id} 
                  to={`/app/bookings/${booking.id}`}
                  className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                >
                  <div>
                    <h4 className="font-medium text-slate-900">{booking.property_title}</h4>
                    <p className="text-sm text-slate-500">
                      {format(new Date(booking.check_in), 'MMM d')} - {format(new Date(booking.check_out), 'MMM d, yyyy')}
                    </p>
                  </div>
                  <div className="text-right">
                    <span className={`
                      px-2 py-1 rounded-full text-xs font-medium
                      ${booking.status === 'paid' ? 'status-paid' : ''}
                      ${booking.status === 'pending' ? 'status-pending' : ''}
                      ${booking.status === 'approved' ? 'status-approved' : ''}
                      ${booking.status === 'cancelled' ? 'status-cancelled' : ''}
                    `}>
                      {booking.status}
                    </span>
                    <p className="text-sm text-slate-500 mt-1">
                      {formatPrice(booking.total_charged)}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-slate-500 mb-4">You haven't made any bookings yet.</p>
              <Link to="/search">
                <Button className="btn-primary">
                  Browse Properties
                </Button>
              </Link>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
